// Транспортные типы agentbox. Зеркало PROTOCOL.md и server/agentbox/protocol.py.
// Менять только вместе с обоими файлами.

export type PermissionMode =
  | 'default' | 'acceptEdits' | 'plan' | 'bypassPermissions' | 'dontAsk' | 'auto'
export type SessionType = 'agent' | 'shell'
/** Роль сессии: обычный чат или закреплённый канал-владелец Telegram-аккаунта. */
export type SessionRole = 'chat' | 'telegram' | 'orchestrator' | 'orchestrator_planner'
export type SessionState = 'idle' | 'running' | 'waiting_permission' | 'exited'
export type LineKind =
  | 'me' | 'sys' | 'agent' | 'ok' | 'warn' | 'tool' | 'result' | 'voice' | 'perm'
  /** вывод панели на слэш-команду (README §2а) — без глифа «⏺» */
  | 'cmd'
  /** `!команда` оператора (ui-plan п.8): shell в cwd сессии, вывод в rows */
  | 'bash'

/** Глифы — те же, что печатает claude code в терминале (см. protocol.py). */
export const DEFAULT_GUTTER: Record<LineKind, string> = {
  me: '>', sys: '·', agent: '⏺', ok: '⏺', warn: '⏺',
  voice: '♪', tool: '⏺', result: '⎿', perm: '⏺', cmd: '>', bash: '!',
}

export const PERMISSION_MODE_LABEL: Record<PermissionMode, string> = {
  default: 'спрашивать',
  acceptEdits: 'правки без спроса',
  plan: 'только план',
  bypassPermissions: 'без ограничений',
  dontAsk: 'не спрашивать (deny)',
  auto: 'решает классификатор',
}

export interface PermissionRequest {
  id: string
  toolName: string
  input: Record<string, unknown>
  suggestions: Record<string, unknown>[]
  createdAt: number
}

/** Вложение хода оператора, как его видит лог (без пути на сервере). */
export interface AttachmentMeta {
  id: string
  name: string
  size: number
  mime: string
  kind: 'image' | 'file'
  /** исполняемый файл — помечается в плашке */
  executable: boolean
}

export interface CommandRow {
  key: string
  value: string
  tone?: '' | 'ok' | 'warn' | 'dim' | 'err'
  /** id беседы: ключ строки — ссылка «открыть» (/resume) */
  open?: string | null
}

/** Структурный ответ на слэш-команду панели (README §2а). */
export interface CommandResult {
  name: string
  args: string
  ok: boolean
  title: string
  rows: CommandRow[]
  notes: string[]
  /** новый разговор (/clear) — рисуется разделителем */
  divider: boolean
}

/** Подсказка при вводе «/»: client/panel выполняет панель, skill/command — агент. */
export interface CommandInfo {
  name: string
  description: string
  /** client — встроенная команда Claude Code, panel — своя команда панели,
   *  skill — скил, command — пользовательская, mcp — промпт MCP-сервера */
  source: 'client' | 'panel' | 'skill' | 'command' | 'mcp'
  argHint: string
  /** кто выполняет: panel — POST …/command, agent — уходит процессу сессии
   *  как есть, local — сам браузер. Старый сервер поля не шлёт. */
  via?: 'panel' | 'agent' | 'local'
}

/** Большая вставка (как в Claude Code): в тексте токен `[Pasted text #N +K lines]`. */
export interface PastedText { id: number; text: string }

/** Путь для автодополнения «@», относительно cwd сессии. */
export interface FileEntry { path: string; dir: boolean }

export interface AttachmentLimits { maxBytes: number; maxFiles: number; blocked: string[] }

export interface Line {
  seq: number
  ts: number
  kind: LineKind
  gutter?: string | null
  text?: string | null
  tool?: string | null
  toolUseId?: string | null
  rows?: string[] | null
  head?: number | null
  partial?: boolean
  perm?: PermissionRequest | null
  /** строка голосового: id сохранённой расшифровки на сервере */
  voiceId?: string | null
  /** kind="me": вложения этого хода */
  attachments?: AttachmentMeta[] | null
  /** kind="me": большие вставки — в text стоят их токены */
  pastes?: PastedText[] | null
  /** kind="me": clientId реплики — по нему снимается страховка доставки */
  clientId?: string | null
  /** kind="cmd": результат команды */
  cmd?: CommandResult | null
  /** kind="bash": код выхода; null — ещё идёт или прервано */
  exitCode?: number | null
}

/** Сохранённая расшифровка голосового (сервер, voice_store.py). */
export interface VoiceItem { id: string; at: number; durationSec: number; text: string }

export interface SessionInfo {
  id: string
  title: string
  /** Заголовок ещё стандартный и будет заменён первой фразой разговора. */
  autoTitle: boolean
  type: SessionType
  state: SessionState
  cwd: string
  /** Профиль-владелец; null — сессия из времён до профилей, её видят все. */
  owner: string | null
  role: SessionRole
  /** Закреплённые сессии поднимаются сами после рестарта сервера. */
  pinned: boolean
  pid: number | null
  permissionMode: PermissionMode
  /** Многоагентный режим: панель подставляет согласие на оркестрацию
   *  в каждое сообщение — в stream-json нет TUI, ловящего «ultracode». */
  ultracode: boolean
  /** субагенты и фоновые задачи: идущие и недавно законченные (старый сервер — нет поля) */
  tasks?: TaskInfo[]
  /** заметка, в которую сохранена беседа; ссылка не исчезает никогда */
  notePath: string | null
  model: string | null
  ctxTokens: number
  ctxPercent: number
  costUsd: number
  /** выводы `!`-команд, которые уйдут агенту следующим ходом (старый сервер — нет поля) */
  bashContext?: number
  /** идёт `!`-команда — Esc прерывает её */
  bashRunning?: boolean
  /** голосовых в расшифровке на сервере — уйдут агенту сами (старый сервер — нет поля) */
  voicePending?: number
  lastActivity: number
  seq: number
  createdAt: number
}

/** Субагент / фоновая задача / отвязанный процесс сессии. */
export interface TaskInfo {
  id: string
  /** general-purpose, Explore, local_bash, local_workflow … */
  kind: string
  label: string
  toolUseId: string | null
  startedAt: number
  toolUses: number
  tokens: number
  lastTool: string | null
  /** идёт / готово / ошибка / остановлена / оборвана рестартом (старый сервер — нет поля) */
  state?: 'running' | 'done' | 'failed' | 'stopped' | 'interrupted'
  /** когда закончилась (epoch, с); готовые висят карточкой ещё 15 мин */
  finishedAt?: number | null
  /** отвязанный процесс (nohup … &) — его pid на сервере */
  pid?: number | null
}

/** Окно лимита подписки: процент израсходованного и когда сбросится. */
export interface UsageWindow { percent: number; resetsAt: string | null }
export interface UsageResponse {
  fiveHour: UsageWindow | null
  sevenDay: UsageWindow | null
  error?: string
}

// ---------- REST ----------

export interface ProfileInfo { name: string; label: string }
export interface AuthResponse {
  token: string
  expiresAt: number
  profile: string
  label: string
}
export interface MeResponse {
  host: string
  expiresAt: number
  profile: string
  label: string
  /** Общий потолок живых сессий на сервере, один на все профили. */
  maxSessions: number
  /** У профиля есть канал Телеграма (владелец сервера). Нет в старых серверах. */
  telegramOwner?: boolean
}
export interface CreateSessionRequest {
  type?: SessionType
  cwd?: string
  title?: string
  prompt?: string
  permissionMode?: PermissionMode
  ultracode?: boolean
  model?: string
  /** инструкции агенту системным промптом (append к пресету claude_code), см. PROTOCOL.md */
  appendSystemPrompt?: string
  /** беседа-сборщик раздела: $AGENTBOX_URL/$AGENTBOX_TOKEN в окружении и curl к своему разделу без вопросов */
  builderSection?: string
}
export interface PatchSessionRequest {
  title?: string
  permissionMode?: PermissionMode
  ultracode?: boolean
  model?: string
}
export interface LogPage { lines: Line[]; seq: number }
export interface PurgeResponse { removed: number }
export interface VoiceResponse {
  text: string
  durationSec: number
  /** пик записи, dBFS (≈ −55 и ниже — микрофон молчал) */
  peakDb?: number | null
  /** сколько секунд звук был «живым» */
  activeSec?: number | null
  /** id сохранённой расшифровки (старый сервер — нет поля) */
  voiceId?: string | null
  /** autosend: true — сервер сам отправил текст агенту, false — не смог (причина
   *  строкой в логе беседы); null/нет — отправлять должен клиент */
  sent?: boolean | null
}

/** Файл, легший в каталог сессии. Агент читает его с диска по `path`. */
export interface AttachmentResponse {
  name: string
  path: string
  size: number
  /** новый сервер: id для input.attachments, тип и ссылка на скачивание */
  id?: string
  mime?: string
  kind?: 'image' | 'file'
  executable?: boolean
  url?: string
}

/** Одна ошибка браузера для POST /api/client-errors (без авторизации).
 *  Поля режутся сервером: message — 2 КБ, stack — 8 КБ, пачка — 20 записей. */
export interface ClientError {
  message: string
  stack?: string | null
  /** error, unhandledrejection, resource, react:<панель>, stall… */
  kind: string
  url?: string | null
  userAgent?: string | null
  at?: number | null
}
export interface ClientErrorBatch { items: ClientError[] }

// ---------- WS: сервер → клиент ----------

export type ServerMessage =
  | { type: 'hello'; session: SessionInfo; pendingPermissions: PermissionRequest[] }
  | { type: 'line'; line: Line }
  | { type: 'delta'; seq: number; append: string; offset?: number }
  | { type: 'rows'; seq: number; append: string[] }
  | { type: 'done'; seq: number; exitCode?: number | null }
  | { type: 'status'; session: SessionInfo }
  | { type: 'perm'; request: PermissionRequest }
  | { type: 'perm_resolved'; id: string; status: 'allowed' | 'denied' | 'expired' }
  | { type: 'pong'; t: number }
  | { type: 'error'; message: string; fatal: boolean }

// ---------- WS: клиент → сервер ----------

export type ClientMessage =
  | {
      type: 'input'
      text: string
      clientId: string
      /** id вложений — уходят агенту этим же ходом */
      attachments?: string[]
      /** заметка «в контексте» — путь в базе профиля */
      note?: string
      /** большие вставки: токены в text сервер развернёт для агента */
      pastes?: PastedText[]
    }
  | { type: 'interrupt' }
  | {
      type: 'perm_response'
      id: string
      behavior: 'allow' | 'deny'
      message?: string | null
      remember?: boolean
      updatedInput?: Record<string, unknown> | null
    }
  | { type: 'set_permission_mode'; mode: PermissionMode }
  | { type: 'ping'; t: number }
  | { type: 'resize'; cols: number; rows: number }

// ---------- база знаний (HUB_CONTRACT.md) ----------

/** Тип заметки: определяет значок в дереве и цвет узла в графе. */
export type NoteType = 'note' | 'goal' | 'project' | 'reference' | 'log' | 'message' | 'task'
export type NoteSource = 'agent' | 'manual' | 'import'
export type NoteStatus = 'planned' | 'in_progress' | 'done' | 'dropped'
/** Трекер сообщений (PLAN.md): как отвечать на входящее. */
export type MessageAction = 'sam' | 'soglasovanie' | 'sebe'

export interface TreeNode {
  /** путь от корня базы профиля: «работа/беседы/итоги.md» */
  path: string
  name: string
  kind: 'note' | 'folder'
  type: NoteType | null
  source: NoteSource | null
  updated: number
  children: TreeNode[]
}

export interface NoteMeta {
  path: string
  title: string
  type: NoteType
  status: NoteStatus | null
  horizon: string | null
  track: string | null
  tags: string[]
  created: number
  updated: number
  source: NoteSource
  /** трекер — заполнены только у type="message"/"task" */
  chatId: string | null
  messageId: number | null
  contact: string | null
  action: MessageAction | null
  link: string | null
}

export interface NoteFull extends NoteMeta {
  /** markdown без frontmatter */
  body: string
  links: string[]
  /** названия из [[...]], которым не соответствует ни одна заметка */
  missing: string[]
  backlinks: NoteMeta[]
}

export interface SearchHit {
  path: string
  title: string
  type: NoteType
  /** фрагмент с маркерами <<< >>> вокруг совпадения */
  snippet: string
  score: number
}

export interface FeedItem {
  path: string
  title: string
  change: 'created' | 'updated'
  source: NoteSource
  at: number
  read: boolean
  summary: string
}

export interface FeedPage { items: FeedItem[]; unread: number }

export interface CreateNoteRequest {
  path: string
  title?: string
  type?: NoteType
  body?: string
}

export interface SaveNoteRequest {
  body?: string
  /** `updated`, с которым читали заметку. Файл на диске новее — сервер ответит
   *  409, а не затрёт чужую правку. Без поля — осознанная перезапись. */
  baseUpdated?: number
  frontmatter?: Partial<Pick<NoteMeta, 'title' | 'type' | 'status' | 'horizon' | 'track' | 'tags'>>
}

// ---------- трекер (PLAN.md «Трекер сообщений из Telegram») ----------

export interface TrackerActionRequest { path: string; action: MessageAction }
export interface TrackerPromoteRequest { path: string }
export interface TrackerStatusRequest { path: string; status: NoteStatus }

// ---------- вторая очередь хаба (HUB_CONTRACT_2.md) ----------

export interface Comment {
  id: string
  author: 'human' | 'agent'
  at: number
  text: string
}

/** Выжимка направления рядом с заметкой: «имя.context.md». */
export interface NoteContext {
  path: string
  decided: string
  open: string
  sessionId: string | null
  updated: number
}

export interface GraphNode {
  /** у ненаписанной заметки путь пустой, ключ — «missing:<название>» */
  path: string
  title: string
  type: NoteType | null
  track: string | null
  /** число входящих связей: по нему считается размер узла */
  incoming: number
  updated: number
  missing: boolean
}

export interface GraphEdge { source: string; target: string; missing: boolean }
export interface Graph { nodes: GraphNode[]; edges: GraphEdge[] }

export interface YonoteCollection { id: string; name: string }
export interface YonoteExport { url: string; documentId: string }
